import { t } from '@lingui/macro';
import { PagedRequestModel } from './Shared';

export enum FleetReportContextType {
  Account = 'acc',
  Sites = 'sites',
}

export const FleetReportContextOptionsFn = (): Record<FleetReportContextType, string> => ({
  [FleetReportContextType.Account]: t`For Account`,
  [FleetReportContextType.Sites]: t`For Selected Sites`,
});

export interface FleetReportMetadataListItem {
  id: number; // Id of report
  description?: string; // Description including account name and report type, line break, names of sites separated by commas (less than 6 sites), or number of sites in format ("x Sites")
  username?: string; // Name of the user
  exportType?: ReportExportType; // type of export (included in description)
  exportStatus?: ReportExecutionStatusType; // Status of the report
  exportFileName?: string; // Name of file
  scheduledReportDate?: string; // Date the report was scheduled
  reportStartDate?: string; // Date the report begins
  reportEndDate?: string; // Date the report ends
  reportRunDate?: string; // Date the report was run successfully
  expires?: string; // Either null or Date in the future the report will expire
  status: ReportExportStatusType; // Status indication of report in more friendly format than exportStatus
  downloadLink?: string; // Full url where the report can be downloaded (if the report is completed successfully) or null
  helpText?: string; // Text to indicate any issues or null - not supplied by the api
}

export type ReportExecutionStatusType = 'Stopped' | 'Success' | 'Expired' | 'Queued' | 'Failed' | 'In Progress';

export type ReportExportType = 'LNSTable' | 'FleetReport' | 'PressviewFleetReport' | 'HydrekaPatrimonyReport';

export enum ReportExportStatusType {
  Queued = 'Queued',
  InProgress = 'InProgress',
  Stopped = 'Stopped',
  Failed = 'Failed',
  Success = 'Success',
  Expired = 'Expired',
  Unknown = 'Unknown',
}

export enum ReportActionEnum {
  Help = 'help',
  Download = 'download',
}

export const ReportActionFn = (): Record<ReportActionEnum, string> => ({
  [ReportActionEnum.Help]: t`Help`,
  [ReportActionEnum.Download]: t`Download`,
});

export const ReportStatusButtons: Record<ReportExportStatusType, ReportActionEnum> = {
  [ReportExportStatusType.Queued]: null,
  [ReportExportStatusType.InProgress]: null,
  [ReportExportStatusType.Success]: ReportActionEnum.Download,
  [ReportExportStatusType.Stopped]: ReportActionEnum.Help,
  [ReportExportStatusType.Failed]: ReportActionEnum.Help,
  [ReportExportStatusType.Expired]: ReportActionEnum.Help,
  [ReportExportStatusType.Unknown]: ReportActionEnum.Help,
};

// Previously provided by the api this was the help text logic - moved to client to allow translatable text
export const ReportHelpDictionaryFn = (): Record<ReportExportStatusType, string> => ({
  [ReportExportStatusType.Queued]: null,
  [ReportExportStatusType.InProgress]: null,
  [ReportExportStatusType.Success]: null,
  [ReportExportStatusType.Unknown]: t`The report status is unknown.  Try requesting the report again`,
  [ReportExportStatusType.Stopped]: t`The report exceeded the maximum attempts.  Request the report again`,
  [ReportExportStatusType.Failed]: t`The report may be for an unsupported type, have missing options, or have encountered an error while processing.  Try requesting the report again`,
  [ReportExportStatusType.Expired]: t`The report has now expired. Request the report again`,
});

export interface ReportsParamsModel extends PagedRequestModel {}

export enum FleetReportDeliveryType {
  Email = 'Email',
  SendToMe = 'SendToMe',
}

export interface FleetReportFormModel extends SchedulingFleetReportFormModel {
  channelSettings: boolean;

  meterReadings: boolean;
  time: string;

  callInSettings: boolean;
  installTimes: boolean;

  callInReport: boolean;
  days: number;
  includeToday: boolean;

  locations: boolean;

  parameterReport: boolean;
  flatlines: boolean;
  overReading: boolean;
  overReadingInput: number;
  negativeValues: boolean;
  zeroValues: boolean;
  period: number;

  alarmSettings: boolean;
  onlySitesInAlarm: boolean;

  sensorSerialNumber: boolean;
  lastBatteryReplacement: boolean;
  nextBatteryReplacement: boolean;
  maintenanceNotes: boolean;
  externalBattery: boolean;
  technologyType: boolean;

  pipeSize: boolean;
  pipeMaterial: boolean;
  valveLidModified: boolean;
  serviceDate: boolean;
  warrantyExpiryDate: boolean;
  equipmentServiceCost: boolean;

  delivery: FleetReportDeliveryType;
  emails: string[];
  fileName: string;

  isLegacyReportType?: boolean;
}

export interface SchedulingFleetReportFormModel {
  scheduleEnabled?: boolean;
  scheduleRecurrency?: ScheduleRecurrenceType;
  scheduledReportDate?: string; // as Date
  scheduleInterval?: number;
  scheduleDay?: number;
  scheduleWeekday?: string;
  scheduleWeek?: string;
  //
  monthlyEveryWeek?: boolean;
}

export enum ScheduleRecurrenceType {
  NoRecurrency = 'NoRecurrency',
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}

export const ScheduleRecurrenceOptionsFn = (): Record<ScheduleRecurrenceType, string> => ({
  [ScheduleRecurrenceType.NoRecurrency]: t`No recurrence`,
  [ScheduleRecurrenceType.Daily]: t`Daily`,
  [ScheduleRecurrenceType.Weekly]: t`Weekly`,
  [ScheduleRecurrenceType.Monthly]: t`Monthly`,
});

export enum ScheduleWeekDayType {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
}

export const ScheduleWeekDayOptionsFn = (): Record<ScheduleWeekDayType, string> => ({
  [ScheduleWeekDayType.Monday]: t`Monday`,
  [ScheduleWeekDayType.Tuesday]: t`Tuesday`,
  [ScheduleWeekDayType.Wednesday]: t`Wednesday`,
  [ScheduleWeekDayType.Thursday]: t`Thursday`,
  [ScheduleWeekDayType.Friday]: t`Friday`,
  [ScheduleWeekDayType.Saturday]: t`Saturday`,
  [ScheduleWeekDayType.Sunday]: t`Sunday`,
});

export enum ScheduleWeekType {
  First = 'First',
  Second = 'Second',
  Third = 'Third',
  Fourth = 'Fourth',
  Last = 'Last',
}

export const ScheduleWeekOptionsFn = (): Record<ScheduleWeekType, string> => ({
  [ScheduleWeekType.First]: t`First`,
  [ScheduleWeekType.Second]: t`Second`,
  [ScheduleWeekType.Third]: t`Third`,
  [ScheduleWeekType.Fourth]: t`Fourth`,
  [ScheduleWeekType.Last]: t`Last`,
});
